/* eslint-disable custom/no-restricted-translation-jsx-untranslated-children-text */
import React from 'react';

import { List, ListItem, ListItemText, Typography } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';

import styles from './Content.module.css';
import { SignUpButton } from './SignUpButton';

interface ContentCityRoomProps {
  cityName: string;
}

export const ContentCityRoom = ({ cityName }: ContentCityRoomProps) => {
  const { locale } = useIntl();

  return locale === Locale.en ? (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        {`Rooms for rent in ${cityName}, Netherlands`}
      </Typography>

      <Typography variant="body1" className={styles.paragraph}>
        {`Looking to rent a room in ${cityName}, Use Kamernet and rent rooms in
            ${cityName} from top letting agents and private homeowners. Our site has
            the largest selection of rooms for rent in ${cityName} for short and
            long-term. Kamernet is the best website to find rooms in ${cityName}
            having thousands of listings that are updated regularly. Our
            platform allows you to choose from a wide variety of rooms to rent
            in ${cityName} online and schedule a viewing instantly. In case if you
            are searching for female room sharing options, then you can also
            expect to find female only rooms to rent in ${cityName} on Kamernet.`}
      </Typography>

      <Typography variant="body1" className={styles.paragraph}>
        {`There are various types of rooms for rent across ${cityName}, but you may
            need to decide on what type of room you wish to live in. With the
            rising cost of living in ${cityName}, the average room rent in ${cityName} is
            also rising due to which sharing rooms in ${cityName} is now becoming an
            economical choice. Here's what you need to know about the different
            types of private rooms and room shares in ${cityName} that you can find
            on Kamernet.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        Private Rooms and Shared Rooms
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Finding private rooms for rent in ${cityName} can be challenging. On our
            platform, you can easily find private rooms in ${cityName} for both
            short-term and long-term rent. Sharing a room in ${cityName} is becoming
            more and more common as it’s a good way to save money and live with
            like-minded roommates in ${cityName}. On Kamernet, you can browse through
            different options of room share in ${cityName} and book your desired
            shared room in ${cityName} that best matches your lifestyle and budget.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        Students Rooms for International Students
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`If you are searching for rooms in ${cityName} for students, then we have
            a wide selection of student rooms in ${cityName} that you can book easily
            and safely. Since affordable student housing in ${cityName} and the
            surrounding area is limited, our platform provides the largest
            selection of student accommodation in Rotterdam at affordable
            prices. Due to the high demand for rooms for international students
            in ${cityName}, we recommend you to create an account on Kamernet to start
            your search and rent a room in ${cityName} for students as quickly as you
            can.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        Furnished and Unfurnished Rooms
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`If you are looking for a furnished room to rent in ${cityName}, then
            Kamernet has the largest selection of fully furnished rooms for rent
            in ${cityName} that are fully equipped and ready to move. Tenants can
            also find semi furnished rooms for rent in ${cityName} with basic
            furnishing for more personalised and convenient living experience.
            Since the rent of a furnished room is higher than an unfurnished
            room, you can also search for an unfurnished room for rent in ${cityName}
            to stay on a tight budget.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        Affordable and Cheap Rooms for Rent
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Finding cheap rooms in ${cityName} can be tough due to high rent prices,
            but on our platform you can find cheap rooms for rent in ${cityName} at
            the lowest prices. Kamernet is the most trusted site that lets you
            discover affordable and cheap single rooms in ${cityName} that are
            perfect fit for your lifestyle and budget.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        Short Term and Long Term Rooms
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`If you're looking to rent a room in ${cityName} for long term, then
            Kamernet will help you find a room for rent in ${cityName} for long term.
            For tenants looking to rent a room in ${cityName} for short term, our
            platform can help you find a room for rent in ${cityName} for short term.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        Pet-Friendly Rooms for Pet Lovers
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Finding pet-friendly rooms in ${cityName} having pets allowed can be
            challenging. Therefore, if you want to live with your pet in your
            shared room, then Kamernet can also help you find pet-friendly rooms
            for rent in ${cityName} that allow pets like cats and dogs.`}
      </Typography>

      <Typography
        variant="h4"
        className={styles.title}
      >{`How to find rooms in ${cityName} with Kamernet?`}</Typography>

      <List component="ol" className={styles.list}>
        <SignUpButton />
        <ListItem className={styles.listItem}>
          <ListItemText primary="Get Kamernet premium account to respond to unlimited adverts" />
        </ListItem>
        <ListItem className={styles.listItem}>
          <ListItemText primary="Choose the plan that's right for you 15-Days, 30-Days, 3-Months" />
        </ListItem>
        <ListItem className={styles.listItem}>
          <ListItemText primary="Enter a payment method to start subscription and respond to adverts." />
        </ListItem>
      </List>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Typography
        variant="h1"
        className={styles.title}
      >{`Kamers te huur in ${cityName}, Nederland`}</Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Op zoek naar een kamer te huur in ${cityName}, Gebruik Kamernet en huur
          kamers in ${cityName} van top verhuurmakelaars en particuliere verhuurders.
          Onze site heeft het grootste aanbod aan kamers te huur in ${cityName} voor
          korte en lange termijn. Kamernet is de beste website om kamers te
          vinden in ${cityName} met duizenden advertenties die regelmatig worden
          bijgewerkt. Op ons platform kun je online kiezen uit een breed aanbod
          aan kamers te huur in ${cityName} en direct een bezichtiging plannen. Als
          je op zoek bent naar kamers voor vrouwen, dan kun je ook kamers te
          huur voor alleen vrouwen in ${cityName} verwachten op Kamernet.`}
      </Typography>
      <Typography>
        {`Er zijn verschillende soorten kamers te huur in ${cityName}, maar het is aan jou om
          te beslissen in wat voor soort kamer je wilt wonen. Met de stijgende
          kosten van levensonderhoud in ${cityName}, stijgt ook de gemiddelde
          kamerhuur in ${cityName}, waardoor het delen van kamers in ${cityName} nu een
          voordelige keuze wordt. Dit is wat je moet weten over de verschillende
          soorten privékamers en gedeelde kamers in ${cityName} die je kunt vinden op
          Kamernet.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Privékamers en gedeelde kamers
      </Typography>
      <Typography
        variant="body1"
        className={styles.paragraph}
      >{`Het vinden van privékamers te huur in ${cityName} kan een uitdaging zijn.
          Op ons platform kun je gemakkelijk privékamers in ${cityName} vinden voor
          zowel korte- als langetermijnverhuur. Een kamer delen in ${cityName} wordt
          steeds gebruikelijker omdat het een goede manier is om geld te
          besparen en samen te wonen met gezellige huisgenoten in ${cityName}. Op
          Kamernet kun je bladeren door verschillende opties van gedeelde kamers
          in ${cityName} en je gewenste gedeelde kamer in ${cityName} huren die het beste
          past bij jouw levensstijl en budget.`}</Typography>
      <Typography variant="h3" className={styles.title}>
        Studentenkamers voor internationale studenten
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Als je op zoek bent naar kamers in ${cityName} voor studenten, dan hebben
          wij een ruime keuze aan studentenkamers in ${cityName} die je gemakkelijk
          en veilig kunt huren. Omdat betaalbare studentenhuisvesting in ${cityName}
          en omgeving beperkt is, biedt ons platform het grootste aanbod aan
          studentenhuisvesting in Rotterdam tegen betaalbare prijzen. Vanwege de
          grote vraag naar kamers voor internationale studenten in ${cityName} raden
          wij je aan om een account aan te maken op Kamernet om je zoektocht te
          starten en zo snel mogelijk een kamer te huren in ${cityName} voor
          studenten.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Gemeubileerde en ongemeubileerde kamers
      </Typography>
      {`Als je op zoek bent naar een gemeubileerde kamer te huur in ${cityName},
          dan heeft ${cityName} het grootste aanbod aan volledig gemeubileerde kamers
          te huur in ${cityName} die volledig zijn ingericht en klaar zijn om te
          verhuizen. Huurders kunnen ook semi-gemeubileerde kamers te huur
          vinden in ${cityName} met een basisinrichting voor een meer persoonlijke en
          comfortabele woonervaring. Omdat de huur van een gemeubileerde kamer
          hoger is dan van een ongemeubileerde kamer, kun je ook een
          ongemeubileerde kamer te huur in ${cityName} zoeken om binnen je budget te
          blijven.`}
      <Typography variant="h3" className={styles.title}>
        Betaalbare en goedkope kamers te huur
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het vinden van goedkope kamers in ${cityName} kan lastig zijn vanwege de
          hoge huurprijzen, maar op ons platform kun je goedkope kamers te huur
          vinden in ${cityName} tegen de laagste prijzen. Kamernet is de meest
          vertrouwde site waarmee je betaalbare en goedkope kamers in ${cityName}
          kunt ontdekken die perfect passen bij jouw levensstijl en budget.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Kamers voor korte en lange termijn
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Als je op zoek bent naar een kamer te huur in ${cityName} voor langere
          termijn, dan helpt Kamernet je bij het vinden van een kamer te huur in
          ${cityName} voor langere termijn. Voor huurders die voor korte termijn een
          kamer in ${cityName} willen huren, kan ons platform je ook helpen bij het
          vinden van een kamer te huur in ${cityName} voor korte termijn.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Huisdiervriendelijke kamers voor dierenliefhebbers
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het vinden van huisdiervriendelijke kamers in ${cityName} waar huisdieren
          zijn toegestaan, kan een uitdaging zijn. Als je daarom met jouw
          huisdier in een gedeelde kamer wilt wonen, kan Kamernet je ook helpen
          bij het vinden van huisdiervriendelijke kamers te huur in ${cityName} waar
          huisdieren zoals katten en honden zijn toegestaan.`}
      </Typography>
      <Typography variant="h4" className={styles.title}>
        {`Hoe vind je kamers in ${cityName} met Kamernet?`}
      </Typography>
      <List component="ol" className={styles.list}>
        <SignUpButton />
        <ListItem className={styles.listItem}>
          <ListItemText
            primary={`Neem een Kamernet premium account om onbeperkt te reageren op
              advertenties`}
          />
        </ListItem>
        <ListItem className={styles.listItem}>
          <ListItemText primary="Kies het plan dat bij je past 15-dagen, 30-dagen, 3 maanden" />
        </ListItem>
        <ListItem className={styles.listItem}>
          <ListItemText
            primary="Voer een betaalmethode in om het abonnement te starten en op
              advertenties te reageren."
          />
        </ListItem>
      </List>
    </React.Fragment>
  );
};
